<template>
    <USlideover
        :model-value="open"
        @update:model-value="$emit('update:open', $event)"
        :ui="{ width: 'max-w-5xl' }"
    >
        <UCard>
            <template #header>
                <div class="flex items-center justify-between">
                    <div>
                        <h3 class="text-base font-medium text-gray-800">{{ batch?.name || 'Batch Details' }}</h3>
                        <p v-if="batch" class="text-sm text-gray-500 mt-0.5">
                            #{{ batch.number.toString().padStart(8, '0') }} - {{ dayjs(batch.created_at).format('MMM D, YYYY HH:mm') }}
                        </p>
                    </div>
                    <UButton
                        v-if="canResendWhatsApp"
                        label="Resend WhatsApp"
                        icon="i-ph-paper-plane-tilt-duotone"
                        size="sm"
                        @click="handleBulkResend"
                        :loading="resending"
                    />
                </div>
            </template>

            <div v-if="batch" class="space-y-6">
                <div class="grid grid-cols-5 gap-4">
                    <div class="bg-gray-50 p-4 rounded-lg">
                        <p class="text-xs text-gray-500 mb-1">Total Records</p>
                        <p class="text-2xl font-mono font-semibold">{{ batch.total_records }}</p>
                    </div>
                    <div class="bg-green-50 p-4 rounded-lg">
                        <p class="text-xs text-green-600 mb-1">Successful</p>
                        <p class="text-2xl font-mono font-semibold text-green-700">{{ batch.successful_count }}</p>
                    </div>
                    <div class="bg-amber-50 p-4 rounded-lg">
                        <p class="text-xs text-amber-600 mb-1">Duplicates</p>
                        <p class="text-2xl font-mono font-semibold text-amber-700">{{ batch.duplicate_count }}</p>
                    </div>
                    <div class="bg-red-50 p-4 rounded-lg">
                        <p class="text-xs text-red-600 mb-1">Errors</p>
                        <p class="text-2xl font-mono font-semibold text-red-700">{{ batch.invalid_count + batch.error_count }}</p>
                    </div>
                    <div class="bg-blue-50 p-4 rounded-lg">
                        <p class="text-xs text-blue-600 mb-1">Signed In</p>
                        <p class="text-2xl font-mono font-semibold text-blue-700">{{ signedInCount }}/{{ batch.successful_count }}</p>
                    </div>
                </div>

                <div class="flex items-center space-x-2">
                    <USelectMenu
                        v-model="selectedStatus"
                        :options="statusOptions"
                        trailing
                    >
                        <template #label>
                            <span>{{ selectedStatus.label }}</span>
                        </template>
                    </USelectMenu>

                    <USelectMenu
                        v-model="signInFilter"
                        :options="signInOptions"
                        trailing
                    >
                        <template #label>
                            <span>{{ signInFilter.label }}</span>
                        </template>
                    </USelectMenu>
                </div>

                <UTable
                    :rows="paginatedRecords"
                    :columns="recordColumns"
                    :loading="loadingRecords"
                    :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                    :empty-state="{ icon: 'i-ph-user-duotone', label: 'No records found.' }"
                >
                    <template #row_index-data="{ row }">
                        <p class="font-mono text-xs">{{ row.row_index + 1 }}</p>
                    </template>
                    <template #full_name-data="{ row }">
                        <ULink
                            v-if="row.customer_id"
                            :to="`/customers/${row.customer_id}`"
                            class="hover:underline"
                        >
                            {{ row.full_name }}
                        </ULink>
                        <span v-else>{{ row.full_name }}</span>
                    </template>
                    <template #family-data="{ row }">
                        <UTooltip v-if="row.group_key" :text="row.group_key">
                            <UBadge
                                :label="row.family_role ? familyRoleLabels[row.family_role] : 'Not linked'"
                                :color="row.family_role ? 'blue' : 'gray'"
                                variant="subtle"
                                size="xs"
                                class="cursor-help"
                            />
                        </UTooltip>
                    </template>
                    <template #phone-data="{ row }">
                        <p v-if="row.phone" class="font-mono text-xs">{{ row.phone }}</p>
                        <p v-else class="text-xs text-gray-400">—</p>
                    </template>
                    <template #status-data="{ row }">
                        <UTooltip
                            v-if="row.error_message"
                            :text="row.error_message"
                        >
                            <UBadge
                                :label="getStatusLabel(row.status)"
                                :color="getStatusColor(row.status)"
                                variant="subtle"
                                size="xs"
                                class="cursor-help"
                            />
                        </UTooltip>
                        <UBadge
                            v-else
                            :label="getStatusLabel(row.status)"
                            :color="getStatusColor(row.status)"
                            variant="subtle"
                            size="xs"
                        />
                    </template>
                    <template #signed_in-data="{ row }">
                        <p v-if="!row.phone" class="text-xs text-gray-400">—</p>
                        <UIcon
                            v-else-if="row.signed_in"
                            name="i-ph-check-circle-fill"
                            class="size-4 text-green-500"
                        />
                        <UIcon
                            v-else
                            name="i-ph-x-circle-fill"
                            class="size-4 text-gray-300"
                        />
                    </template>
                    <template #whatsapp-data="{ row }">
                        <p v-if="!row.phone" class="text-xs text-gray-400">—</p>
                        <UTooltip
                            v-else-if="row.whatsapp_sent && row.resend_count > 0"
                            :text="`Sent ${row.resend_count + 1} time${row.resend_count > 0 ? 's' : ''} • Last: ${dayjs(row.last_resent_at).format('MMM D, HH:mm')}`"
                        >
                            <div class="flex items-center space-x-0.5 cursor-help">
                                <UIcon
                                    v-for="i in Math.min(row.resend_count + 1, 5)"
                                    :key="i"
                                    name="i-ph-check-circle-fill"
                                    class="size-4 text-green-500"
                                />
                                <span v-if="row.resend_count + 1 > 5" class="text-xs text-gray-500 ml-1">
                                    +{{ row.resend_count + 1 - 5 }}
                                </span>
                            </div>
                        </UTooltip>
                        <UIcon
                            v-else-if="row.whatsapp_sent"
                            name="i-ph-check-circle-fill"
                            class="size-4 text-green-500"
                        />
                        <UIcon
                            v-else
                            name="i-ph-x-circle-fill"
                            class="size-4 text-red-500"
                        />
                    </template>
                    <template #actions-data="{ row }">
                        <UButton
                            v-if="canResend(row)"
                            icon="i-ph-paper-plane-tilt-duotone"
                            size="2xs"
                            color="gray"
                            @click="resendWhatsApp(row)"
                            :loading="row.resending"
                        />
                    </template>
                </UTable>

                <TablePagination :pagination="recordsPagination" />
            </div>
        </UCard>
    </USlideover>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import type { BulkActivationBatch, BulkActivationRecord } from '~/types/bulkActivation'

const props = defineProps<{
    open: boolean
    batch?: BulkActivationBatch
}>()

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const client = useSupabaseClient()
const toast = useToast()
const recordsPagination = usePagination(100)

const loadingRecords = ref(false)
const resending = ref(false)
const records = ref<BulkActivationRecord[]>([])
const selectedStatus = ref({ label: 'All Statuses', value: 'all' })
const signInFilter = ref({ label: 'All Customers', value: 'all' })

const statusOptions = [
    { label: 'All Statuses', value: 'all' },
    { label: 'Success', value: 'success' },
    { label: 'Duplicate', value: 'duplicate' },
    { label: 'Invalid Data', value: 'invalid_data' },
    { label: 'No Credential Match', value: 'no_credential_match' },
    { label: 'Active Subscription', value: 'active_subscription_exists' },
    { label: 'No Family Head', value: 'no_family_head' },
    { label: 'Family Not Linked', value: 'family_link_failed' },
    { label: 'Other Errors', value: 'error' },
]

const familyRoleLabels: Record<string, string> = {
    parent: 'Head',
    member: 'Member',
    child: 'Dependent',
}

const signInOptions = [
    { label: 'All Customers', value: 'all' },
    { label: 'Signed In', value: 'signed_in' },
    { label: 'Not Signed In', value: 'not_signed_in' },
]

const hasFamilies = computed(() => records.value.some(r => r.group_key))

const recordColumns = computed(() => [
    { key: 'row_index', label: 'Row' },
    { key: 'full_name', label: 'Name' },
    ...(hasFamilies.value ? [{ key: 'family', label: 'Family' }] : []),
    { key: 'phone', label: 'Phone' },
    { key: 'status', label: 'Status' },
    { key: 'signed_in', label: 'Signed In' },
    { key: 'whatsapp', label: 'WhatsApp' },
    { key: 'actions', class: 'w-px' },
])

const paginatedRecords = computed(() => {
    const start = recordsPagination.page * recordsPagination.limit
    const end = start + recordsPagination.limit
    return records.value.slice(start, end)
})

const signedInCount = computed(() => {
    return records.value.filter(r => r.status === 'success' && r.signed_in).length
})

function canResend(record: BulkActivationRecord) {
    return (record.status === 'success' || record.status === 'family_link_failed')
        && !!record.phone
        && !record.signed_in
}

const canResendWhatsApp = computed(() => {
    return records.value.some(canResend)
})

watch(() => props.open, async (open) => {
    if (open && props.batch) {
        await fetchRecords()
    }
})

watch([selectedStatus, signInFilter], () => {
    fetchRecords()
})

async function fetchRecords() {
    if (!props.batch) return

    loadingRecords.value = true

    const statusParam = selectedStatus.value.value === 'all' || selectedStatus.value.value === 'error'
        ? undefined
        : selectedStatus.value.value

    const signInParam = signInFilter.value.value === 'all'
        ? undefined
        : signInFilter.value.value

    const { data, error } = await client.rpc('get_bulk_activation_records', {
        p_batch_id: props.batch.id,
        p_status: statusParam,
        p_signed_in_filter: signInParam,
        p_limit: 10000,
        p_offset: 0
    } as never)

    if (error) {
        console.error('Failed to fetch records:', error)
    }

    if (data) {
        let filteredData = data as any[]

        if (selectedStatus.value.value === 'error') {
            filteredData = (data as any[]).filter((r: any) =>
                r.status === 'auth_creation_failed' ||
                r.status === 'customer_creation_failed' ||
                r.status === 'subscription_creation_failed'
            )
        }

        records.value = filteredData.map((r: any) => ({
            ...r,
            resending: false
        })) as BulkActivationRecord[]

        recordsPagination.setTotal(records.value.length)
        recordsPagination.page = 0
    }

    loadingRecords.value = false
}

async function resendWhatsApp(record: BulkActivationRecord) {
    const confirmed = confirm(`Send WhatsApp welcome message to ${record.full_name} (${record.phone})?`)

    if (!confirmed) return

    record.resending = true

    try {
        const { error } = await client.functions.invoke('resend-whatsapp', {
            body: {
                recordId: record.id,
                phone: record.phone,
                partnerId: props.batch?.partner_id
            }
        })

        if (error) throw error

        toast.add({
            title: 'WhatsApp message sent',
            color: 'green',
            icon: 'i-ph-check-circle-duotone'
        })

        await fetchRecords()
    } catch (error: any) {
        toast.add({
            title: 'Failed to send WhatsApp',
            description: error.message,
            color: 'red',
            icon: 'i-ph-warning-duotone'
        })
    }

    record.resending = false
}

async function handleBulkResend() {
    const eligibleRecords = records.value.filter(canResend)

    if (eligibleRecords.length === 0) {
        toast.add({
            title: 'No eligible customers',
            description: 'All customers have already signed in.',
            color: 'amber',
            icon: 'i-ph-warning-duotone'
        })
        return
    }

    const confirmed = confirm(`Send WhatsApp welcome messages to ${eligibleRecords.length} customer${eligibleRecords.length > 1 ? 's' : ''}?`)

    if (!confirmed) return

    resending.value = true

    try {
        const { data, error } = await client.functions.invoke('bulk-resend-whatsapp', {
            body: {
                batchId: props.batch?.id,
                recordIds: eligibleRecords.map(r => r.id)
            }
        })

        if (error) throw error

        toast.add({
            title: 'WhatsApp messages sent',
            description: `Sent to ${data.sent} customers${data.failed > 0 ? `, ${data.failed} failed` : ''}.`,
            color: 'green',
            icon: 'i-ph-check-circle-duotone'
        })

        await fetchRecords()
    } catch (error: any) {
        toast.add({
            title: 'Failed to send messages',
            description: error.message,
            color: 'red',
            icon: 'i-ph-warning-duotone'
        })
    }

    resending.value = false
}

function getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
        success: 'Success',
        duplicate: 'Duplicate',
        invalid_data: 'Invalid Data',
        no_credential_match: 'No Credential',
        active_subscription_exists: 'Already Active',
        auth_creation_failed: 'Phone number taken',
        customer_creation_failed: 'Customer Failed',
        subscription_creation_failed: 'Subscription Failed',
        whatsapp_failed: 'WhatsApp Failed',
        no_family_head: 'No Family Head',
        family_link_failed: 'Family Not Linked'
    }
    return labels[status] || status
}

function getStatusColor(status: string): string {
    const colors: Record<string, string> = {
        success: 'green',
        duplicate: 'amber',
        invalid_data: 'red',
        no_credential_match: 'red',
        active_subscription_exists: 'amber',
        auth_creation_failed: 'red',
        customer_creation_failed: 'red',
        subscription_creation_failed: 'red',
        whatsapp_failed: 'orange',
        no_family_head: 'red',
        family_link_failed: 'amber'
    }
    return colors[status] || 'gray'
}
</script>
